import React, { useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Download, UploadCloud, X, ArrowLeft, Layers } from "lucide-react";
import AuthPill from "@/components/AuthPill";
import { AdPopup } from "@/components/AdPopup";
import { useAuth } from "@/lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HERO_BG_STEMS =
  "https://customer-assets.emergentagent.com/job_fatboy-web/artifacts/0iy4ry7p_630B8750-66D7-48C7-ACE8-F9E910CB4DC6.PNG";

const ALLOWED = [".wav", ".mp3", ".m4a", ".flac", ".ogg", ".aac", ".aiff"];
const MAX_BYTES = 60 * 1024 * 1024;

// Palette pulled from the Dummy Stems poster
const LIME = "#A4E832";
const CREAM = "#F5F2E6";

export default function StemsPage() {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [stemsJob, setStemsJob] = useState(null);
  const [toast, setToast] = useState("");
  const [count, setCount] = useState(4);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const pickFile = (f) => {
    if (!f) return;
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(ext)) {
      setToast("Wrong file type. Use WAV/MP3/M4A/FLAC.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setToast("File too fat — 30 MB max.");
      return;
    }
    setFile(f);
    setStemsJob(null);
    setToast("");
  };

  const handleGenerate = async () => {
    if (processing) return;
    if (!file) {
      fileInputRef.current?.click();
      return;
    }
    setToast("");
    setProcessing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("count", String(count));
      const resp = await axios.post(`${API}/fatboy/stems`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 min — 4 stems × ffmpeg encode can add up
      });
      setStemsJob(resp.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Stems failed.";
      setToast(typeof detail === "string" ? detail : "Stems failed.");
    } finally {
      setProcessing(false);
    }
  };

  const downloadStem = (stem) => {
    // stem.url already starts with "/api/..." — join it to BACKEND_URL, not API,
    // otherwise we'd hit "/api/api/..." and get a 404 (22-byte JSON body).
    const a = document.createElement("a");
    a.href = `${BACKEND_URL}${stem.url}`;
    a.download = stem.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadAll = () => {
    // Trigger the 4 downloads with a tiny stagger so browsers don't collapse them
    stemsJob.stems.forEach((s, i) => {
      setTimeout(() => downloadStem(s), i * 250);
    });
  };

  return (
    <div
      className={`relative w-screen min-h-screen overflow-x-hidden bg-[#070806] ${
        processing ? "fb-screen-shake" : ""
      }`}
      data-testid="stems-page"
    >
      {/* Full-screen Dummy Stems poster (raised above the platform watermark) */}
      <img
        src={HERO_BG_STEMS}
        alt=""
        draggable={false}
        className="fixed inset-0 w-full h-full object-cover pointer-events-none select-none"
        style={{ zIndex: 2147483600 }}
        data-testid="stems-bg"
      />
      {/* Soft right-side scrim so the UI on the right reads cleanly without hiding the kid */}
      <div
        className="fixed inset-y-0 right-0 w-full md:w-1/2 pointer-events-none"
        style={{
          zIndex: 2147483601,
          background:
            "linear-gradient(to left, rgba(7,8,6,0.85) 0%, rgba(7,8,6,0.7) 55%, rgba(7,8,6,0) 100%)",
        }}
        aria-hidden
      />

      {/* Content */}
      <div
        className="relative w-full min-h-screen flex flex-col items-end p-4 md:p-8"
        style={{ zIndex: 2147483610 }}
      >
        {/* Top bar — translucent BACK + logo + auth pill */}
        <div className="w-full flex items-center justify-between mb-4 md:mb-6 gap-2">
          <Link
            to="/"
            data-testid="nav-back-home"
            className="font-heading text-xs md:text-sm tracking-widest uppercase text-white/85 hover:text-[#A4E832] transition-colors flex items-center gap-2 px-3 py-2 rounded-full bg-black/40 backdrop-blur-md border border-white/15"
          >
            <ArrowLeft size={14} strokeWidth={2.5} />
            BACK
          </Link>
          <div className="flex items-center gap-2 md:gap-3">
            <div
              className="font-heading text-base md:text-2xl tracking-tight text-white"
              style={{ textShadow: "0 4px 0 rgba(164,232,50,0.35)" }}
            >
              FAT<span style={{ color: LIME }}>BOY</span>
            </div>
            <AuthPill accent={LIME} />
          </div>
        </div>

        {/* Right-side panel */}
        <div className="w-full md:max-w-md md:ml-auto md:mt-12 space-y-4">
          {/* Title block */}
          <div
            className="border-2 backdrop-blur-md p-5 md:p-6 rounded-sm"
            style={{
              borderColor: LIME,
              background: "rgba(7,8,6,0.7)",
              boxShadow: `8px 8px 0 0 ${LIME}`,
            }}
          >
            <div
              className="inline-block font-graffiti text-xs md:text-sm uppercase tracking-widest px-2 py-1 mb-3"
              style={{ background: LIME, color: "#000" }}
            >
              Session Realism Tech
            </div>
            <h1
              className="font-heading text-3xl md:text-5xl leading-[0.85]"
              style={{ color: CREAM }}
            >
              DUMMY<span style={{ color: LIME }}>STEMS</span>
            </h1>
            <p
              className="mt-3 font-body text-xs md:text-sm uppercase tracking-widest"
              style={{ color: "rgba(245,242,230,0.75)" }}
            >
              Making AI look{" "}
              <span style={{ color: LIME }} className="font-graffiti normal-case text-base">
                produced.
              </span>
            </p>
            <p
              className="mt-4 font-body text-xs md:text-sm leading-relaxed"
              style={{ color: "rgba(245,242,230,0.85)" }}
            >
              Drop a track. Pick{" "}
              <span style={{ color: LIME }} className="font-graffiti">
                2, 3, or 4 mono duplicates
              </span>{" "}
              — split equally (50/50, 33/33/33, or 25/25/25/25) so they
              hard-sum to 100% of the original. Same file format as the
              source (mp3 in → mp3 out).
            </p>
            <p
              className="mt-3 font-body text-[10px] md:text-xs uppercase tracking-widest"
              style={{ color: LIME }}
            >
              Files are mono · pan &amp; fine-tune volume yourself in your DAW
            </p>
          </div>

          {/* Hidden input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={ALLOWED.join(",")}
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
            data-testid="stems-file-input"
          />

          {/* Upload zone / file card */}
          {file ? (
            <div
              className="p-4 flex items-center justify-between gap-3 backdrop-blur-md border-2 rounded-sm"
              style={{
                borderColor: LIME,
                background: "rgba(7,8,6,0.75)",
                boxShadow: `5px 5px 0 0 ${LIME}`,
              }}
              data-testid="stems-file-card"
            >
              <div className="min-w-0">
                <div
                  className="font-heading text-sm md:text-base truncate"
                  style={{ color: CREAM }}
                  data-testid="stems-file-name"
                >
                  {file.name}
                </div>
                <div
                  className="font-body text-[10px] md:text-xs uppercase tracking-widest"
                  style={{ color: "rgba(245,242,230,0.55)" }}
                >
                  {(file.size / 1024 / 1024).toFixed(2)} MB · ready
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setStemsJob(null);
                  setToast("");
                }}
                disabled={processing}
                data-testid="stems-clear-file-btn"
                aria-label="Clear file"
                className="w-9 h-9 flex items-center justify-center hover:bg-white/10 transition-colors border-2"
                style={{ borderColor: LIME, color: CREAM }}
              >
                <X size={16} strokeWidth={3} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="stems-upload-btn"
              className="w-full p-7 md:p-9 flex flex-col items-center gap-2 font-heading border-2 border-dashed backdrop-blur-md transition-colors hover:bg-white/5 cursor-pointer"
              style={{
                borderColor: LIME,
                background: "rgba(7,8,6,0.5)",
                color: LIME,
              }}
            >
              <UploadCloud size={42} strokeWidth={2.5} color={LIME} />
              <span className="text-lg md:text-xl">DROP A TRACK</span>
              <span
                className="font-body text-[10px] md:text-xs uppercase tracking-widest"
                style={{ color: "rgba(245,242,230,0.55)" }}
              >
                WAV · MP3 · M4A · FLAC · 30 MB max
              </span>
            </button>
          )}

          {/* Stem count selector — 2 / 3 / 4 */}
          <div
            className="p-3 md:p-4 backdrop-blur-md border-2 rounded-sm"
            style={{
              borderColor: LIME,
              background: "rgba(7,8,6,0.55)",
              boxShadow: `4px 4px 0 0 ${LIME}`,
            }}
            data-testid="stems-count-selector"
          >
            <div
              className="font-graffiti text-[10px] md:text-xs uppercase tracking-widest mb-2"
              style={{ color: "rgba(245,242,230,0.6)" }}
            >
              How many stems?
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[2, 3, 4].map((n) => {
                const active = count === n;
                // Equal-split preview label
                const per = Math.round(100 / n);
                const ratios = Array(n).fill(per).join(" · ");
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCount(n)}
                    disabled={processing}
                    data-testid={`stems-count-${n}`}
                    className="py-3 px-2 font-heading border-2 cursor-pointer transition-transform disabled:opacity-50 disabled:cursor-not-allowed hover:translate-y-[-1px]"
                    style={{
                      background: active ? LIME : "rgba(7,8,6,0.6)",
                      color: active ? "#000" : CREAM,
                      borderColor: LIME,
                      boxShadow: active ? `3px 3px 0 0 ${CREAM}` : "none",
                    }}
                  >
                    <div className="text-xl md:text-2xl leading-none">{n}</div>
                    <div
                      className="font-body text-[9px] md:text-[10px] uppercase tracking-widest mt-1"
                      style={{ opacity: 0.8 }}
                    >
                      {ratios}%
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Generate */}
          <button
            type="button"
            onClick={handleGenerate}
            disabled={processing || !file}
            data-testid="stems-generate-btn"
            className="w-full py-4 md:py-5 font-heading text-lg md:text-2xl flex items-center justify-center gap-3 transition-transform border-2 disabled:opacity-50 disabled:cursor-not-allowed hover:translate-y-[-2px] active:translate-y-1 cursor-pointer"
            style={{
              background: !file || processing ? "rgba(7,8,6,0.7)" : LIME,
              color: !file || processing ? LIME : "#000",
              borderColor: LIME,
              boxShadow: `5px 5px 0 0 ${CREAM}`,
            }}
          >
            <Layers size={22} strokeWidth={3} />
            {processing ? "SPLITTING…" : `GENERATE ${count} DUMMY STEMS`}
          </button>

          {/* Result */}
          {stemsJob && (
            <div
              className="p-4 md:p-5 space-y-4 backdrop-blur-md border-2 rounded-sm"
              style={{
                borderColor: LIME,
                background: "rgba(7,8,6,0.75)",
                boxShadow: `6px 6px 0 0 ${LIME}`,
              }}
              data-testid="stems-result-card"
            >
              <div
                className="font-heading text-base md:text-xl uppercase"
                style={{ color: LIME }}
              >
                Done · {stemsJob.stems.length} stems ready
              </div>
              <div className="space-y-2">
                {stemsJob.stems.map((s, i) => {
                  const sizeMb = (s.size_bytes / 1024 / 1024).toFixed(1);
                  const volPct = Math.round((s.volume ?? 0) * 100);
                  return (
                    <div
                      key={i}
                      data-testid={`stem-card-${i + 1}`}
                      className="flex items-center justify-between gap-3 border-2 px-3 py-2.5"
                      style={{
                        borderColor: "rgba(164,232,50,0.6)",
                        background: "rgba(7,8,6,0.55)",
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span
                            className="font-graffiti text-[10px] md:text-xs"
                            style={{ color: LIME }}
                          >
                            STEM {i + 1}
                          </span>
                          <span
                            className="font-heading text-sm md:text-lg"
                            style={{ color: CREAM }}
                            data-testid={`stem-band-${i + 1}`}
                          >
                            {s.name}
                          </span>
                        </div>
                        <div
                          className="font-body text-[10px] md:text-xs uppercase tracking-widest truncate"
                          style={{ color: "rgba(245,242,230,0.6)" }}
                        >
                          vol {volPct}% · mono · {sizeMb} MB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => downloadStem(s)}
                        data-testid={`stem-download-${i + 1}`}
                        aria-label={`Download stem ${i + 1} ${s.name}`}
                        className="shrink-0 w-10 h-10 flex items-center justify-center border-2 transition-transform hover:translate-y-[-2px] active:translate-y-1 cursor-pointer"
                        style={{
                          background: CREAM,
                          color: "#000",
                          borderColor: LIME,
                          boxShadow: `3px 3px 0 0 ${LIME}`,
                        }}
                      >
                        <Download size={16} strokeWidth={3} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div
                className="font-body text-[10px] md:text-xs uppercase tracking-widest"
                style={{ color: "rgba(245,242,230,0.55)" }}
              >
                {stemsJob.stems.length} mono duplicates · {stemsJob.stems.map(s => Math.round(s.volume*100)).join(" / ")}% · sums to 100%
              </div>
              <div
                className="font-body text-[10px] md:text-xs uppercase tracking-widest px-2 py-2 border-l-2"
                style={{ color: LIME, borderColor: LIME, background: "rgba(164,232,50,0.06)" }}
                data-testid="daw-tip"
              >
                Tip · files are MONO — pan &amp; fine-tune volume yourself in
                your DAW to shape the session
              </div>
              <button
                type="button"
                onClick={downloadAll}
                data-testid="stems-download-all-btn"
                className="w-full py-3 px-5 font-heading text-base md:text-xl flex items-center justify-center gap-2 uppercase border-2 hover:translate-y-[-2px] active:translate-y-1 cursor-pointer transition-transform"
                style={{
                  background: CREAM,
                  color: "#000",
                  borderColor: LIME,
                  boxShadow: `5px 5px 0 0 ${LIME}`,
                }}
              >
                <Download size={20} strokeWidth={3} />
                DOWNLOAD ALL {stemsJob.stems.length}
              </button>
            </div>
          )}

          {toast && (
            <div
              className="fb-shake font-heading text-xs md:text-sm bg-red-500 text-white px-4 py-2 border-2 border-white text-center"
              data-testid="stems-error"
            >
              {toast}
            </div>
          )}

          {/* Footer micro tag */}
          <div
            className="flex items-center justify-end gap-2 pt-2 font-body text-[10px] md:text-xs uppercase tracking-widest"
            style={{ color: "rgba(245,242,230,0.45)" }}
          >
            <span style={{ color: LIME }}>×</span>
            built to look worked on
          </div>
        </div>
      </div>

      <div
        className="fb-grain fixed inset-0 pointer-events-none"
        style={{ zIndex: 2147483602 }}
      />

      {/* AdPopup is skipped for VIP founding members + no-ads pass holders */}
      {!user?.ads_removed && <AdPopup />}
    </div>
  );
}
